/**
 * Allergens Select Component
 *
 * Multi-select checkbox grid for selecting allergens.
 */

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const COMMON_ALLERGENS = [
  'Gluten',
  'Crustaces',
  'Oeufs',
  'Poisson',
  'Arachides',
  'Soja',
  'Lait',
  'Fruits a coque',
  'Celeri',
  'Moutarde',
  'Sesame',
  'Sulfites',
  'Lupin',
  'Mollusques',
];

interface AllergensSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
}

export function AllergensSelect({ value, onChange, label }: AllergensSelectProps) {
  const toggleAllergen = (allergen: string) => {
    if (value.includes(allergen)) {
      onChange(value.filter((a) => a !== allergen));
    } else {
      onChange([...value, allergen]);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-dark-500 mb-3">
          {label}
        </label>
      )}

      <div className="grid grid-cols-2 gap-2">
        {COMMON_ALLERGENS.map((allergen) => {
          const isSelected = value.includes(allergen);
          return (
            <label
              key={allergen}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                isSelected
                  ? 'border-warning-400 bg-warning-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              )}
            >
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleAllergen(allergen)}
                  className="sr-only"
                />
                <div
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded border transition-colors',
                    isSelected
                      ? 'border-warning-500 bg-warning-500'
                      : 'border-neutral-300 bg-white'
                  )}
                >
                  {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                </div>
              </div>
              <span className="text-sm text-dark-500">{allergen}</span>
            </label>
          );
        })}
      </div>

      {value.length > 0 && (
        <p className="mt-2 text-sm text-warning-600">
          {value.length} allergene{value.length > 1 ? 's' : ''} selectionne{value.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
